import { useCallback, useEffect, useState } from "react";
import { ScanEye } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, streamAnalysis } from "@/lib/api";
import { Header } from "@/components/Header";
import { Dropzone } from "@/components/Dropzone";
import { MediaPreview } from "@/components/MediaPreview";
import { AnalysisView } from "@/components/AnalysisView";
import { HistoryList } from "@/components/HistoryList";
import { EmptyState } from "@/components/EmptyState";
import { HomeShowcase } from "@/components/HomeShowcase";

const IMG = ["jpg", "jpeg", "png", "webp"];
const VID = ["mp4", "mov", "webm"];

export default function Studio() {
  const [media, setMedia] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => api.get("/analyses").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setPreview = (next) => setMedia((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return next; });

  const pickFile = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    const type = VID.includes(ext) ? "video" : IMG.includes(ext) ? "image" : null;
    if (!type) return toast.error("Formato non supportato. Usa JPG, PNG, WEBP, MP4, MOV o WEBM.");
    const max = type === "video" ? 100 : 20;
    if (file.size > max * 1024 * 1024) return toast.error(`File troppo grande: massimo ${max} MB per ${type === "video" ? "i video" : "le immagini"}.`);
    setAnalysis(null);
    setPreview({ file, url: URL.createObjectURL(file), type, name: file.name });
  };

  const reset = () => { if (phase === "idle") { setPreview(null); setAnalysis(null); } };

  const analyze = async () => {
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", media.file);
      const { data } = await api.post("/analyses", fd);
      setAnalysis({ ...data, content: "" });
      setPhase("streaming");
      loadHistory();
      let failed = false;
      await streamAnalysis(data.id, (ev) => {
        if (ev.type === "delta") setAnalysis((a) => ({ ...a, content: a.content + ev.content }));
        if (ev.type === "done") setAnalysis((a) => ({ ...a, prompt: ev.prompt, status: "done" }));
        if (ev.type === "error") { failed = true; toast.error(ev.message); }
      });
      if (!failed) toast.success("Analisi completata e salvata");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setPhase("idle");
      loadHistory();
    }
  };

  const openItem = async (id) => {
    if (phase !== "idle") return;
    try {
      const [{ data }, file] = await Promise.all([api.get(`/analyses/${id}`), api.get(`/analyses/${id}/file`, { responseType: "blob" })]);
      setPreview({ url: URL.createObjectURL(file.data), type: data.media_type, name: data.filename });
      setAnalysis(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/analyses/${id}`);
      if (analysis?.id === id) { setAnalysis(null); setPreview(null); }
      toast.success("Analisi eliminata");
      loadHistory();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const onPromptSaved = (prompt) => { setAnalysis((a) => ({ ...a, prompt })); loadHistory(); };
  const busy = phase !== "idle";
  const canAnalyze = media?.file && !analysis && !busy;

  return (
    <div className="min-h-screen">
      <Header onNew={() => { if (!busy) { setPreview(null); setAnalysis(null); } }} />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-14">
        <div className="min-w-0 lg:col-span-5 space-y-12">
          <div className="space-y-4">
            <p className="rf-mono text-[11px] tracking-[0.3em] uppercase rf-red">Studio</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95]">Scomponi.<br />Ricostruisci.</h1>
          </div>
          {media ? (
            <MediaPreview url={media.url} type={media.type} name={media.name} onClear={reset} locked={busy} />
          ) : (
            <Dropzone onFile={pickFile} disabled={busy} />
          )}
          {media && (
            <div className="flex flex-wrap gap-3">
              {media.file && !analysis && (
                <button onClick={analyze} disabled={!canAnalyze} className="rf-btn" data-testid="analyze-start-button">
                  <ScanEye size={15} /> {phase === "uploading" ? "Caricamento…" : "Analizza"}
                </button>
              )}
              {(analysis || !media.file) && !busy && (
                <button onClick={() => { setPreview(null); setAnalysis(null); }} className="rf-btn rf-btn-ghost" data-testid="new-analysis-button">Nuova analisi</button>
              )}
            </div>
          )}
          <HistoryList items={history} activeId={analysis?.id} onOpen={openItem} onDelete={deleteItem} />
        </div>
        <div className="min-w-0 lg:col-span-7">
          {analysis && (analysis.content || phase === "streaming") ? (
            <AnalysisView analysis={analysis} streaming={phase === "streaming"} onPromptSaved={onPromptSaved} />
          ) : !media && phase === "idle" ? (
            <HomeShowcase />
          ) : (
            <EmptyState phase={phase} hasMedia={!!media} failed={analysis?.status === "error"} />
          )}
        </div>
      </main>
    </div>
  );
}
